import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../../components/FeatherIcon'
import { addRecent, cleanSearch } from './discoverData'

/** This viewer's recent Discover searches (a convenience in this browser, like OnlyFans' own). */
const RECENT_KEY = 'ofDiscoverRecentSearches'

function readRecent(): string[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]')
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

function writeRecent(list: string[]) {
  try {
    if (list.length) localStorage.setItem(RECENT_KEY, JSON.stringify(list))
    else localStorage.removeItem(RECENT_KEY)
  } catch {
    /* storage unavailable: recent searches just aren't kept */
  }
}

interface Props {
  /** The search in effect (the route's `q`). */
  value: string
  onSearch: (q: string) => void
  /** ✕: leave search (clears `q`). */
  onClose: () => void
}

/** The header's search form (DiscoverSearch): ✕, the field with recent searches, the search button. */
export function DiscoverSearch({ value, onSearch, onClose }: Props) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'discoverPage' })
  const [text, setText] = useState(value)
  const [open, setOpen] = useState(false)
  const [recent, setRecent] = useState(readRecent)
  const [selected, setSelected] = useState(-1)
  const input = useRef<HTMLInputElement | null>(null)
  const form = useRef<HTMLFormElement | null>(null)

  // Opened without a search: focus and show the recent ones.
  useEffect(() => {
    if (value) return
    input.current?.focus()
    setOpen(true)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!value) setText('')
  }, [value])

  // A click outside the form closes the list.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (form.current && e.target instanceof Node && !form.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const shown = text.trim() ? recent.filter((r) => r.toLowerCase().includes(text.trim().toLowerCase())) : recent

  const run = (q: string) => {
    const next = addRecent(recent, q)
    setRecent(next)
    writeRecent(next)
    setText(q)
    setOpen(false)
    setSelected(-1)
    input.current?.blur()
    onSearch(q)
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (selected >= 0 && shown[selected]) return run(shown[selected])
    const q = text.trim()
    if (q === value) {
      setOpen(false)
      input.current?.blur()
      return
    }
    run(q)
  }

  const clearField = () => {
    setText('')
    setSelected(-1)
    if (open) input.current?.focus()
    else setOpen(true)
    if (value) onSearch('')
  }

  const forget = (item: string | null) => {
    const next = item === null ? [] : recent.filter((r) => r !== item)
    setRecent(next)
    writeRecent(next)
    input.current?.focus()
  }

  return (
    <form ref={form} className="ofdc-search" role="search" onSubmit={submit}>
      <button type="button" className="lib-icon-btn" aria-label={t('close')} title={t('close')} onClick={onClose}>
        <FeatherIcon name="x" size={20} />
      </button>
      <div className="ofdc-search-field">
        <input
          ref={input}
          type="search"
          className="ofp-input ofdc-search-input"
          maxLength={100}
          autoComplete="off"
          spellCheck={false}
          placeholder={t('searchPlaceholder')}
          aria-label={t('searchPlaceholder')}
          value={text}
          onChange={(e) => {
            setText(cleanSearch(e.target.value))
            setSelected(-1)
            setOpen(true)
          }}
          onClick={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setSelected((s) => Math.min(s + 1, shown.length - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setSelected((s) => Math.max(s - 1, -1))
            }
          }}
        />
        {text.length > 0 && (
          <button type="button" className="ofdc-search-clear" aria-label={t('clear')} onClick={clearField}>
            <FeatherIcon name="x" size={14} />
          </button>
        )}
        {open && (
          <div className="lib-pop ofdc-auto">
            {shown.length > 0 ? (
              <>
                <div className="ofdc-auto-head">
                  <span>{t('recent')}</span>
                  <button type="button" className="ofp-link-btn" onClick={() => forget(null)}>
                    {t('clearAll')}
                  </button>
                </div>
                <div role="listbox" aria-label={t('recent')}>
                  {shown.map((r, i) => (
                    <div key={r} role="option" aria-selected={i === selected} className={`ofdc-auto-row ${i === selected ? 'is-on' : ''}`} onClick={() => run(r)}>
                      <FeatherIcon name="search" size={15} />
                      <span className="ofdc-auto-text">{r}</span>
                      <button
                        type="button"
                        className="ofdc-auto-remove"
                        aria-label={t('remove')}
                        onClick={(e) => {
                          e.stopPropagation()
                          forget(r)
                        }}
                      >
                        <FeatherIcon name="x" size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="ofdc-auto-empty">{t('searchResults')}</p>
            )}
          </div>
        )}
      </div>
      <button type="submit" className="lib-icon-btn" aria-label={t('search')} title={t('search')} disabled={!text.trim()}>
        <FeatherIcon name="search" size={19} />
      </button>
    </form>
  )
}
